import { useState } from "react";
import { Download, Share as ShareIcon, X, Check } from "lucide-react";
import { usePwaInstall } from "./usePwaInstall";
import { toast } from "sonner";

/**
 * Install App button — renders in one of two variants:
 *  - variant="sidebar" — full-width chip inside the desktop sidebar footer
 *  - variant="compact" — icon-only pill for the mobile top bar
 *
 * On Chrome/Android: fires the deferred beforeinstallprompt.
 * On iOS Safari: opens a small modal explaining "Share → Add to Home Screen"
 *   (iOS never fires beforeinstallprompt).
 * When already installed (running standalone): renders nothing.
 */
export default function InstallAppButton({ variant = "sidebar" }) {
  const { canPrompt, isInstalled, isIOS, install } = usePwaInstall();
  const [showIOS, setShowIOS] = useState(false);

  if (isInstalled) return null;
  // Don't show a dead button on desktop browsers that can't install and
  // aren't iOS Safari — nothing would happen if we did.
  if (!canPrompt && !isIOS) return null;

  const trigger = async () => {
    if (canPrompt) {
      const ok = await install();
      if (ok) toast.success("Fuelled added to your home screen");
      return;
    }
    if (isIOS) setShowIOS(true);
  };

  const label = "Install app";

  if (variant === "compact") {
    return (
      <>
        <button
          onClick={trigger}
          data-testid="pwa-install-btn-compact"
          className="text-[11px] uppercase tracking-widest text-orange-300 hover:text-orange-200 flex items-center gap-1"
        >
          <Download className="h-3.5 w-3.5" /> Install
        </button>
        {showIOS && <IOSHowTo onClose={() => setShowIOS(false)} />}
      </>
    );
  }

  return (
    <>
      <button
        onClick={trigger}
        data-testid="pwa-install-btn"
        className="w-full flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-medium bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/30 text-orange-200 mb-2"
      >
        <Download className="h-4 w-4" />
        <span className="flex-1 text-left">{label}</span>
        <span className="text-[9px] uppercase tracking-widest text-orange-300/80">new</span>
      </button>
      {showIOS && <IOSHowTo onClose={() => setShowIOS(false)} />}
    </>
  );
}

function IOSHowTo({ onClose }) {
  return (
    <div
      data-testid="pwa-ios-modal"
      className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-2xl bg-neutral-950 border border-neutral-800 p-5 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="pwa-ios-close"
          className="absolute top-3 right-3 h-8 w-8 rounded-full flex items-center justify-center text-neutral-500 hover:text-neutral-200 hover:bg-neutral-900"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="text-sm font-semibold mb-1">Add Fuelled to your home screen</div>
        <div className="text-[11px] text-neutral-500 mb-4">
          Safari on iOS installs apps a little differently. Two taps and you're done.
        </div>
        <ol className="space-y-3 text-[13px] text-neutral-300">
          <li className="flex items-start gap-3">
            <span className="h-6 w-6 rounded-full bg-neutral-900 border border-neutral-800 flex items-center justify-center text-[11px] font-semibold">
              1
            </span>
            <div>
              Tap the <ShareIcon className="inline h-3.5 w-3.5 mx-0.5 -mt-0.5" /> <b>Share</b> icon in Safari's toolbar.
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span className="h-6 w-6 rounded-full bg-neutral-900 border border-neutral-800 flex items-center justify-center text-[11px] font-semibold">
              2
            </span>
            <div>
              Scroll and pick <b>Add to Home Screen</b>, then confirm.
            </div>
          </li>
          <li className="flex items-start gap-3">
            <span className="h-6 w-6 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center">
              <Check className="h-3.5 w-3.5 text-emerald-300" />
            </span>
            <div>Fuelled will live on your home screen like any other app.</div>
          </li>
        </ol>
        <button
          onClick={onClose}
          data-testid="pwa-ios-got-it"
          className="mt-5 w-full rounded-xl bg-orange-500 hover:bg-orange-400 text-white text-sm font-medium py-2.5"
        >
          Got it
        </button>
      </div>
    </div>
  );
}
